/*
 * convert object keys from regular camelCase to Microsoft PascalCase
 */
export const convertObjectKeys = (object: any): any => {
  const output: any = {};
  Object.entries(object).forEach(([key, item]) => {
    output[capitalize(key)] = item;
  });
  return output;
};

/*
 * capitalize a string
 */
export const capitalize = (s: string): string => {
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const toTitleCase = (s: string): string =>
  s.replace(/\w\S*/g, (text: string) => {
    return text.charAt(0).toUpperCase() + text.substr(1).toLowerCase();
  });

const isMergeableObject = (val: any) => {
  const nonNullObject = val && typeof val === 'object';

  return (
    nonNullObject &&
    Object.prototype.toString.call(val) !== '[object RegExp]' &&
    Object.prototype.toString.call(val) !== '[object Date]'
  );
};

const emptyTarget = (val: any) => {
  return Array.isArray(val) ? [] : {};
};

const cloneIfNecessary = (value: any, optionsArgument: any) => {
  const clone = optionsArgument && optionsArgument.clone === true;
  return clone && isMergeableObject(value)
    ? deepMerge(emptyTarget(value), value, optionsArgument)
    : value;
};

const defaultArrayMerge = (target: any, source: any, optionsArgument: any) => {
  const destination = target.slice();
  source.forEach((e: any, i: string) => {
    if (typeof destination[i] === 'undefined') {
      destination[i] = cloneIfNecessary(e, optionsArgument);
    } else if (isMergeableObject(e)) {
      destination[i] = deepMerge(target[i], e, optionsArgument);
    } else if (target.indexOf(e) === -1) {
      destination.push(cloneIfNecessary(e, optionsArgument));
    }
  });
  return destination;
};

const mergeObject = (target: any, source: any, optionsArgument: any) => {
  const destination = {};
  if (isMergeableObject(target)) {
    Object.keys(target).forEach(key => {
      destination[key] = cloneIfNecessary(target[key], optionsArgument);
    });
  }
  Object.keys(source).forEach(key => {
    if (!isMergeableObject(source[key]) || !target[key]) {
      destination[key] = cloneIfNecessary(source[key], optionsArgument);
    } else {
      destination[key] = deepMerge(target[key], source[key], optionsArgument);
    }
  });
  return destination;
};

export const deepMerge = (target: any, source: any, optionsArgument: any) => {
  const array = Array.isArray(source);
  const options = optionsArgument || { arrayMerge: defaultArrayMerge };
  const arrayMerge = options.arrayMerge || defaultArrayMerge;

  if (array) {
    return Array.isArray(target)
      ? arrayMerge(target, source, optionsArgument)
      : cloneIfNecessary(source, optionsArgument);
  } else {
    return mergeObject(target, source, optionsArgument);
  }
};

export const deepMergeAll = (array: any, optionsArgument: any) => {
  if (!Array.isArray(array) || array.length < 2) {
    throw new Error('first argument should be an array with at least two elements');
  }
  return array.reduce((prev, next) => {
    return deepMerge(prev, next, optionsArgument);
  });
};
